/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import qs from 'query-string'
import {useLocation, useParams} from 'react-router-dom'
import {
  applyEntraAuthSession,
  beginAzureSsoPkceLogin,
  clearEntraSilentAttempt,
  exchangeAzureAuthorizationCode,
  getStoredEntraLoginHint,
  LogOut,
} from '../redux/AuthCRUD'
import {useDispatch} from 'react-redux'
import {PermissionsClient} from '../../../infrastracture/api/PermissionsClient'
import * as auth from '../redux/AuthRedux'
import {AccountSettingsClient} from '../../../infrastracture/api/AccountSettingsClient'
import {loadAndClearPkceSession, PkceSession} from '../pkce/pkce'

function safeReturnPath(path?: string) {
  return path && path.startsWith('/') && !path.startsWith('//') ? path : '/'
}

function tryLoadPkceSession(state: unknown): PkceSession | null {
  try {
    return state ? loadAndClearPkceSession(String(state)) : null
  } catch {
    return null
  }
}

export function SsoLanding() {
  const location = useLocation()
  const dispatch = useDispatch()
  const params = useParams<{accountId: string}>()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    let cancelled = false

    async function completeSso() {
      const query = qs.parse(location.search, {ignoreQueryPrefix: true})
      const {code, state, error, error_description} = query

      if (error) {
        // A failed silent sign-in (no Entra session, or no matching account) falls back to the interactive one
        const failedSession = tryLoadPkceSession(state)
        if (failedSession?.silent) {
          await beginAzureSsoPkceLogin({
            tenantId: failedSession.tenantId,
            clientId: failedSession.clientId,
            accountId: failedSession.accountId,
            scope: failedSession.scope,
            loginHint: getStoredEntraLoginHint(),
            returnPath: failedSession.returnPath,
          })
          return
        }

        setStatus('error')
        setErrorMessage(String(error_description || error))
        return
      }

      if (!code || !state) {
        setStatus('error')
        setErrorMessage('Missing authorization code. Please sign in again.')
        return
      }

      const routeAccountId = params.accountId
      if (!routeAccountId) {
        setStatus('error')
        setErrorMessage('Invalid SSO callback.')
        return
      }

      try {
        const pkce = loadAndClearPkceSession(String(state))
        if (pkce.accountId !== routeAccountId) {
          throw new Error('Account mismatch. Please sign in again.')
        }

        const entraTokens = await exchangeAzureAuthorizationCode({
          tenantId: pkce.tenantId,
          clientId: pkce.clientId,
          code: String(code),
          redirectUri: pkce.redirectUri,
          codeVerifier: pkce.verifier,
          scope: pkce.scope,
        })

        await applyEntraAuthSession(routeAccountId, entraTokens, {
          tenantId: pkce.tenantId,
          clientId: pkce.clientId,
          scope: pkce.scope,
        })

        if (!localStorage.getItem('acess-token') || cancelled) {
          throw new Error('Could not complete sign-in. Please try again.')
        }

        clearEntraSilentAttempt()

        const accessToken = localStorage.getItem('acess-token') || ''
        dispatch(auth.actions.login(accessToken))

        const permissions = await PermissionsClient.ForCurrentUser()
        if (!cancelled && permissions.successful && permissions.data) {
          const mappedPermissions = permissions.data.data.map((p: any) => p.id)
          dispatch(auth.actions.setUserPermission(mappedPermissions))
        }

        const features = await AccountSettingsClient.GetFeatures()
        if (!cancelled && features.successful && features.data) {
          dispatch(
            auth.actions.setTenantFeatures(
              features.data.filter((a: any) => a.status === 'enabled').map((f: any) => f.id)
            )
          )
        }

        if (!cancelled) {
          window.location.href = safeReturnPath(pkce.returnPath)
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error')
          setErrorMessage(err instanceof Error ? err.message : 'Sign-in failed. Please try again.')
        }
      }
    }

    completeSso()

    return () => {
      cancelled = true
    }
  }, [location.search, params.accountId, dispatch])

  if (status === 'error') {
    return (
      <div className='text-center'>
        <h1 className='text-dark mb-5'>Sign-in failed</h1>
        <p className='text-muted mb-5'>{errorMessage}</p>
        {/* Full reload after clearing storage, so a leftover signed-in state can't bounce /login back to / */}
        <a href='/login' className='btn btn-primary' onClick={() => LogOut()}>
          Return to sign in
        </a>
      </div>
    )
  }

  return (
    <div className='text-center'>
      <h1 className='text-dark'>Please wait while we sign you in...</h1>
    </div>
  )
}
